import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, UserIcon, PhoneIcon, MapPinIcon, SparklesIcon, ClockIcon, AlertCircleIcon, LockIcon, MessageCircleIcon, Globe2Icon } from 'lucide-react';
const locations = ['New York, NY – 58 E 79th St, 4R, New York, NY 10075'];
const treatments = ['Lipo 360', 'BBL (Brazilian Butt Lift)', 'Face Tightening / Dermalift', 'Weight Loss (Ozempic/Semaglutide)', 'Laser Lipo', 'Skin Rejuvenation', 'Body Contouring', 'Other'];
const languages = ['Spanish', 'English', 'Portuguese', 'Other'];
type LeadPayload = {
  name: string;
  phone: string;
  location: string;
  treatment: string;
  language: string;
  whatsappOptIn: boolean;
};
const WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbzEwa9X1kD_eQs6LTVlbm-Ut2f2oDZ3JW54lhUvEmTUmwEBSENr43SNCp12EyuCftLp/exec';
export function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    treatment: '',
    language: '',
    whatsappOptIn: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            hours: prev.hours,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const payload: LeadPayload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        location: formData.city,
        treatment: formData.treatment,
        language: formData.language,
        whatsappOptIn: formData.whatsappOptIn
      };
      console.log('Sending payload:', payload);
      console.log('Webhook URL:', WEBHOOK_URL);
      fetch(WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        console.log('Fetch completed (no-cors mode, errors are expected)', err);
      });
      console.log('Request sent successfully (no-cors mode)');
      // Track Lead event in Meta Pixel with enhanced debugging
      console.log('🔍 Attempting to track Lead event...');
      console.log('🔍 window.fbq exists?', typeof window !== 'undefined' && typeof window.fbq !== 'undefined');
      if (typeof window !== 'undefined' && window.fbq) {
        const leadData = {
          source: 'Dr. Mark Joy MD Consultation',
          domain: window.location.hostname,
          language: formData.language,
          treatment: formData.treatment,
          value: 0,
          currency: 'USD'
        };
        console.log('✅ Tracking Lead event with data:', leadData);
        window.fbq('track', 'Lead', leadData);
        console.log('✅ Lead event tracked successfully!');
      } else {
        console.error('❌ Facebook Pixel (fbq) not found! Pixel may be blocked or not loaded.');
      }
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        city: '',
        treatment: '',
        language: '',
        whatsappOptIn: true
      });
      setTimeout(() => setSubmitStatus('idle'), 10000);
    } catch (error) {
      console.error('Unexpected error:', error);
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        city: '',
        treatment: '',
        language: '',
        whatsappOptIn: true
      });
      setTimeout(() => setSubmitStatus('idle'), 10000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  return <section id="lead-form" className="w-full py-20 lg:py-24 bg-gradient-to-br from-white via-brand-aqua/10 to-white relative overflow-hidden" data-id="element-159">
      <div className="absolute inset-0" data-id="element-160">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/10 rounded-full blur-3xl" data-id="element-161" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-162" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-163">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mx-auto mb-12" data-id="element-164">
          <div className="relative bg-gradient-to-r from-brand-navy via-brand-deep to-brand-navy rounded-2xl p-4 shadow-2xl border border-brand-gold/30 overflow-hidden" data-id="element-165">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" data-id="element-166" />

            <div className="relative flex items-center justify-center gap-3" data-id="element-167">
              <div className="flex-shrink-0" data-id="element-168">
                <div className="w-8 h-8 rounded-full bg-brand-gold/20 backdrop-blur-sm flex items-center justify-center border border-brand-gold/40" data-id="element-169">
                  <AlertCircleIcon className="w-5 h-5 text-brand-gold" strokeWidth={2} data-id="element-170" />
                </div>
              </div>
              <p className="text-white font-bold text-base sm:text-lg tracking-wide" data-id="element-171">
                Professional Medical Consultations Available
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-172">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-173">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 rounded-full mb-6" data-id="element-174">
              <SparklesIcon className="w-4 h-4 text-brand-gold" data-id="element-175" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-176">
                Medical Consultation
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-navy mb-6 leading-tight" data-id="element-177">
              Request Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-gold" data-id="element-178">
                Medical Appointment
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-4 leading-relaxed" data-id="element-179">
              Complete this form to request a consultation with Dr. Mark Joy,
              MD.
            </p>
            <p className="text-base text-gray-500 mb-10 leading-relaxed" data-id="element-180">
              Submitting this form does not confirm an appointment. A medical
              coordinator will contact you to schedule your consultation.
            </p>

            <div className="bg-gradient-to-br from-brand-navy via-brand-deep to-brand-navy rounded-3xl p-8 mb-10 shadow-2xl border border-brand-gold/20" data-id="element-181">
              <div className="flex items-center gap-3 mb-6" data-id="element-182">
                <ClockIcon className="w-6 h-6 text-brand-gold" data-id="element-183" />
                <p className="text-white font-bold text-lg" data-id="element-184">
                  Response within 24 hours
                </p>
              </div>
              <div className="space-y-3" data-id="element-185">
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-brand-gold/20" data-id="element-186">
                  <CheckCircleIcon className="w-5 h-5 text-brand-gold" data-id="element-187" />
                  <span className="text-white/90 text-sm" data-id="element-188">
                    Professional medical evaluation
                  </span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-brand-gold/20" data-id="element-189">
                  <CheckCircleIcon className="w-5 h-5 text-brand-gold" data-id="element-190" />
                  <span className="text-white/90 text-sm" data-id="element-191">
                    Confidential consultation
                  </span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-brand-gold/20" data-id="element-192">
                  <CheckCircleIcon className="w-5 h-5 text-brand-gold" data-id="element-193" />
                  <span className="text-white/90 text-sm" data-id="element-194">
                    English & Spanish available
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-8" data-id="element-195">
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-196">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-197" />
                <span className="text-gray-700 font-semibold" data-id="element-198">
                  Licensed Medical Doctor
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-199">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-200" />
                <span className="text-gray-700 font-semibold" data-id="element-201">
                  Professional Medical Standards
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-202">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-203" />
                <span className="text-gray-700 font-semibold" data-id="element-204">
                  Patient-Focused Approach
                </span>
              </div>
            </div>

            <div className="hidden lg:block" data-id="element-205">
              <img src="https://cdn.magicpatterns.com/uploads/jAq8wneKSqQHBb8jbXFqYN/Screenshot_2026-01-09_at_1.22.18_PM.png" alt="Professional medical office - Dr. Mark Joy MD consultation space" className="rounded-3xl shadow-2xl ring-2 ring-brand-gold/20 object-cover w-full h-[400px]" data-id="element-206" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-207">
            <AnimatePresence mode="wait" data-id="element-208">
              {submitStatus === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.4
            }} className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-green-300 ring-4 ring-green-100" data-id="element-209">
                  <div className="text-center" data-id="element-210">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6 shadow-xl" data-id="element-211">
                      <CheckCircleIcon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-212" />
                    </div>

                    <h3 className="text-3xl font-bold text-green-800 mb-4" data-id="element-213">
                      Request Received
                    </h3>

                    <p className="text-lg text-green-700 font-medium mb-4 leading-relaxed" data-id="element-214">
                      Your consultation request has been successfully submitted.
                    </p>

                    <p className="text-base text-gray-700 mb-4 leading-relaxed" data-id="element-215">
                      A medical coordinator will contact you within 24 hours to
                      schedule your appointment with Dr. Mark Joy, MD.
                    </p>

                    <p className="text-base text-gray-600 leading-relaxed" data-id="element-216">
                      All consultations are confidential and conducted
                      professionally.
                    </p>
                  </div>
                </motion.div> : <motion.form key="form" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-brand-primary/20 ring-4 ring-brand-gold/10" data-id="element-217">
                  <AnimatePresence data-id="element-218">
                    {submitStatus === 'error' && <motion.div initial={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className="mb-6 bg-red-50 border-2 border-red-300 rounded-2xl p-5" data-id="element-219">
                        <p className="text-red-700 font-bold text-center text-base" data-id="element-220">
                          ❌ There was a problem submitting your request. Please
                          try again.
                        </p>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="text-center mb-8" data-id="element-221">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-gold rounded-3xl mb-4 shadow-lg" data-id="element-222">
                      <SparklesIcon className="w-8 h-8 text-white" data-id="element-223" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-224">
                      Schedule Your Consultation
                    </h3>
                    <p className="text-gray-600" data-id="element-225">
                      Professional response within 24 hours
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-226">
                    <div data-id="element-227">
                      <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-228">
                        Full Name <span className="text-red-500" data-id="element-229">*</span>
                      </label>
                      <div className="relative" data-id="element-230">
                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-231" />
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300" required disabled={isSubmitting} data-id="element-232" />
                      </div>
                    </div>

                    <div data-id="element-233">
                      <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-234">
                        Phone Number <span className="text-red-500" data-id="element-235">*</span>
                      </label>
                      <div className="relative" data-id="element-236">
                        <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-237" />
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300" required disabled={isSubmitting} data-id="element-238" />
                      </div>
                    </div>

                    <div data-id="element-239">
                      <label htmlFor="city" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-240">
                        Preferred Location{' '}
                        <span className="text-red-500" data-id="element-241">*</span>
                      </label>
                      <div className="relative" data-id="element-242">
                        <MapPinIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-243" />
                        <select id="city" name="city" value={formData.city} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-244">
                          <option value="" data-id="element-245">Select a location</option>
                          {locations.map(location => <option key={location} value={location} data-id="element-246">
                              {location}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-247">
                      <label htmlFor="treatment" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-248">
                        Treatment of Interest{' '}
                        <span className="text-red-500" data-id="element-249">*</span>
                      </label>
                      <div className="relative" data-id="element-250">
                        <SparklesIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-251" />
                        <select id="treatment" name="treatment" value={formData.treatment} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-252">
                          <option value="" data-id="element-253">Select a treatment</option>
                          {treatments.map(treatment => <option key={treatment} value={treatment} data-id="element-254">
                              {treatment}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-255">
                      <label htmlFor="language" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-256">
                        Preferred Language{' '}
                        <span className="text-red-500" data-id="element-257">*</span>
                      </label>
                      <div className="relative" data-id="element-258">
                        <Globe2Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-259" />
                        <select id="language" name="language" value={formData.language} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-260">
                          <option value="" data-id="element-261">Select your language</option>
                          {languages.map(language => <option key={language} value={language} data-id="element-262">
                              {language}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div className="bg-[#25D366]/10 rounded-2xl p-4 border-2 border-[#25D366]/30" data-id="element-263">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-264">
                        <input type="checkbox" id="whatsappOptIn" name="whatsappOptIn" checked={formData.whatsappOptIn} onChange={handleChange} className="mt-1 w-5 h-5 text-[#25D366] border-gray-300 rounded focus:ring-[#25D366]" required disabled={isSubmitting} data-id="element-265" />
                        <div className="flex-1" data-id="element-266">
                          <div className="flex items-center gap-2 mb-1" data-id="element-267">
                            <MessageCircleIcon className="w-5 h-5 text-[#25D366]" data-id="element-268" />
                            <span className="text-sm font-bold text-gray-800" data-id="element-269">
                              Confirm via WhatsApp{' '}
                              <span className="text-red-500" data-id="element-270">*</span>
                            </span>
                          </div>
                          <p className="text-xs text-gray-600" data-id="element-271">
                            I agree to receive confirmation and reminders via
                            WhatsApp
                          </p>
                        </div>
                      </label>
                    </div>

                    <motion.button type="submit" disabled={isSubmitting} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="w-full py-4 bg-gradient-to-r from-lime-400 to-lime-500 text-gray-900 font-bold text-base rounded-2xl transition-all duration-300 shadow-2xl shadow-lime-400/60 hover:shadow-lime-400/80 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group" data-id="element-272">
                      <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-273">
                        {isSubmitting ? <>
                            <ClockIcon className="w-5 h-5 animate-spin" data-id="element-274" />
                            Sending...
                          </> : <>
                            <SparklesIcon className="w-5 h-5" data-id="element-275" />
                            Submit Appointment Request
                            <span className="text-lg" data-id="element-276">→</span>
                          </>}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-lime-500 to-lime-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" data-id="element-277" />
                    </motion.button>

                    <p className="text-center text-sm text-gray-500 mt-3" data-id="element-278">
                      All consultations are confidential and conducted
                      professionally.
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs" data-id="element-279">
                    <span className="flex items-center gap-1.5 text-gray-600" data-id="element-280">
                      <CheckCircleIcon className="w-4 h-4 text-brand-gold" data-id="element-281" />
                      <span className="font-medium" data-id="element-282">24h Response</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-600" data-id="element-283">
                      <LockIcon className="w-4 h-4 text-brand-gold" data-id="element-284" />
                      <span className="font-medium" data-id="element-285">100% Confidential</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-600" data-id="element-286">
                      <CheckCircleIcon className="w-4 h-4 text-brand-gold" data-id="element-287" />
                      <span className="font-medium" data-id="element-288">No Obligation</span>
                    </span>
                  </div>
                </motion.form>}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>;
}